import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import FocusRadarChart from '../FocusRadarChart/FocusRadarChart';
import { FOCUS_LOOKUP, CATEGORY_DEFINITIONS, ASSESSMENT_DESCRIPTIONS, ASSESSMENT_SHORT_LABELS, filterAssessmentsBySex } from '../../data/categoryDefinitions';
import { useCachedAssessmentTemplates } from '../../hooks/useCachedData';
import './FocusDashboard.css';

function getLatestDate(summary, assessmentIds) {
    if (!summary?.assessments) return null;
    let latest = null;
    for (const aid of assessmentIds) {
        const entry = summary.assessments[aid];
        if (entry?.completed_at) {
            if (!latest || entry.completed_at > latest) latest = entry.completed_at;
        }
    }
    return latest;
}

function getOverallDirection(summary, assessmentIds) {
    if (!summary?.assessments) return null;
    let improving = 0, worsening = 0;
    for (const aid of assessmentIds) {
        const entry = summary.assessments[aid];
        if (!entry?.sections) continue;
        for (const sec of Object.values(entry.sections)) {
            if (sec.direction === 'improving') improving++;
            else if (sec.direction === 'worsening') worsening++;
        }
    }
    if (improving === 0 && worsening === 0) return null;
    if (improving > worsening) return 'improving';
    if (worsening > improving) return 'worsening';
    return 'stable';
}

function FocusInfoModal({ categoryId, focuses, assessmentIds, color, onClose }) {
    const category = CATEGORY_DEFINITIONS[categoryId];
    const { assessmentTemplates } = useCachedAssessmentTemplates();

    return createPortal(
        <div className="fd-info-backdrop" onClick={onClose}>
            <div className="fd-info-modal" onClick={e => e.stopPropagation()}>
                <div className="fd-info-header" style={{ borderBottomColor: color }}>
                    <h3>{category.display_name}</h3>
                    <button className="fd-info-close" onClick={onClose}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 6L6 18" /><path d="M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <p className="fd-info-desc">{category.description}</p>

                <div className="fd-info-section">
                    <h4>Your Focus Areas</h4>
                    {focuses.map(fId => {
                        const f = FOCUS_LOOKUP[fId];
                        if (!f) return null;
                        return (
                            <div key={fId} className="fd-info-focus">
                                <span className="fd-info-focus-name" style={{ color }}>{f.display_name}</span>
                                <span className="fd-info-focus-desc">{f.description}</span>
                            </div>
                        );
                    })}
                </div>

                <div className="fd-info-section">
                    <h4>Radar Chart Assessments</h4>
                    <p className="fd-info-sub">Each point on the spider chart represents one of these assessments.</p>
                    {assessmentIds.map(aid => {
                        const tmpl = assessmentTemplates?.[aid];
                        const name = tmpl?.display_name || ASSESSMENT_SHORT_LABELS[aid] || aid;
                        const desc = ASSESSMENT_DESCRIPTIONS[aid] || tmpl?.description || '';
                        return (
                            <div key={aid} className="fd-info-assessment">
                                <span className="fd-info-assessment-name">{name}</span>
                                <span className="fd-info-assessment-desc">{desc}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>,
        document.body
    );
}

export default function FocusDashboard({ focuses, summary, prevSummary, sex, demo = false }) {
    const navigate = useNavigate();
    const [showInfoCategory, setShowInfoCategory] = useState(null);

    const categoryGroups = useMemo(() => {
        if (!focuses || focuses.length === 0) return [];

        const grouped = {};
        for (const fId of focuses) {
            const focus = FOCUS_LOOKUP[fId];
            if (!focus) continue;
            const cId = focus.categoryId;
            if (!grouped[cId]) {
                grouped[cId] = {
                    categoryId: cId,
                    categoryColor: focus.color,
                    categoryName: CATEGORY_DEFINITIONS[cId]?.display_name,
                    focusIds: [],
                    assessmentIdSet: new Set(),
                };
            }
            grouped[cId].focusIds.push(fId);
            for (const aid of focus.assessment_ids) {
                grouped[cId].assessmentIdSet.add(aid);
            }
        }

        return Object.values(grouped).map(g => {
            const filtered = filterAssessmentsBySex([...g.assessmentIdSet], sex);
            return { ...g, allAssessmentIds: filtered, focusData: { assessment_ids: filtered } };
        });
    }, [focuses, sex]);

    if (categoryGroups.length === 0) return (
        <div className="fd-container">
            <h3 className="fd-heading">Your Focus</h3>
            <div className="fd-card fd-card-empty">
                <div className="frc-placeholder">
                    <span className="frc-placeholder-text">
                        Select your health focus areas to generate your personalized dashboard
                    </span>
                    <button className="fd-select-focus-btn" onClick={() => navigate('/select-focus')}>
                        Choose Focus Areas
                    </button>
                </div>
            </div>
        </div>
    );

    const infoGroup = showInfoCategory
        ? categoryGroups.find(g => g.categoryId === showInfoCategory)
        : null;

    return (
        <div className="fd-container">
            <h3 className="fd-heading">Your Focus</h3>
            <div className="fd-grid">
                {categoryGroups.map(group => (
                    <CategoryCard
                        key={group.categoryId}
                        group={group}
                        summary={summary}
                        prevSummary={prevSummary}
                        onInfo={() => setShowInfoCategory(group.categoryId)}
                        onTakeAssessments={() => navigate('/assessment')}
                        demo={demo}
                    />
                ))}
            </div>
            {infoGroup && (
                <FocusInfoModal
                    categoryId={infoGroup.categoryId}
                    focuses={infoGroup.focusIds}
                    assessmentIds={infoGroup.allAssessmentIds}
                    color={infoGroup.categoryColor}
                    onClose={() => setShowInfoCategory(null)}
                />
            )}
        </div>
    );
}

function CategoryCard({ group, summary, prevSummary, onInfo, onTakeAssessments, demo = false }) {
    const { categoryId, categoryColor, categoryName, focusIds, allAssessmentIds, focusData } = group;
    const lastDate = getLatestDate(summary, allAssessmentIds);
    const direction = getOverallDirection(summary, allAssessmentIds);

    const hasOutstanding = useMemo(() => {
        if (!summary?.assessments) return true;
        const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
        return allAssessmentIds.some(aid => {
            const entry = summary.assessments[aid];
            if (!entry?.completed_at) return true;
            return new Date(entry.completed_at).getTime() < cutoff;
        });
    }, [summary, allAssessmentIds]);

    return (
        <div className="fd-card">
            <div className="fd-card-accent" style={{ backgroundColor: categoryColor }} />
            <div className="fd-card-header">
                <span className="fd-card-name">{categoryName}</span>
                <div className="fd-card-header-right">
                    {direction && (
                        <span className={`fd-direction fd-direction-${direction}`}>
                            {direction === 'improving' ? '↑' : direction === 'worsening' ? '↓' : '→'}
                        </span>
                    )}
                    <button className="fd-info-btn" onClick={onInfo} title="About this focus">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4" />
                            <path d="M12 8h.01" />
                        </svg>
                    </button>
                </div>
            </div>
            <div className="fd-focuses-list">
                {focusIds.map(fId => {
                    const f = FOCUS_LOOKUP[fId];
                    if (!f) return null;
                    return (
                        <span key={fId} className="fd-focus-pill" style={{ backgroundColor: categoryColor }}>
                            {f.display_name}
                        </span>
                    );
                })}
            </div>
            <FocusRadarChart focusData={focusData} summary={summary} prevSummary={prevSummary} color={categoryColor} />
            <div className="fd-card-footer">
                {lastDate && (
                    <span className="fd-card-date">
                        Last updated {new Date(lastDate).toLocaleDateString()}
                    </span>
                )}
                {(hasOutstanding || demo) && (
                    <button
                        className="fd-select-focus-btn"
                        onClick={onTakeAssessments}
                        disabled={demo}
                        title={demo ? 'Demo — not available' : undefined}
                    >
                        Take Assessments
                    </button>
                )}
            </div>
        </div>
    );
}
